/* eslint-disable @next/next/no-img-element */
// @ts-nocheck

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Navigation } from "swiper";
import { useRouter } from "next/router";
import { Grid } from "antd";

import { Flex, RATypography } from "@/components/customs";
import { getSettings } from "@/stores/slices/settingSlice";
import {
	SEChevronLeft,
	SEChevronRight,
	SEClock,
	SEPartnerFleetFavicon,
	SERAFleetFavicon,
	SESpeedometer,
	SEUserGroup,
} from "@/components/svgs";
import { AircraftOwnershipTag } from "@/components/molecules";
import { WorldMapRange } from "@/components/molecules/WorldMapRange";
import { AIRCRAFT_LOCATIONS, AIRCRAFT_PROVIDER } from "@/config/constants";
import s from "./AircraftDropdown.module.css";
import { PATH_ROUTES } from "@/config/routes";

const { useBreakpoint } = Grid;

export const AircraftDropdown = ({
	open,
	onUpdateOpenAircraft,
}: {
	open: boolean;
	onUpdateOpenAircraft: (o: boolean) => void;
}) => {
	const swiperRef = useRef(null);
	const router = useRouter();
	const { xl } = useBreakpoint();

	const { aircraft_categories = [] } = useSelector(getSettings);

	const [selectedCategory, setSelectedCategory] = useState(aircraft_categories?.[0] ?? null);
	const [location, setLocation] = useState(AIRCRAFT_LOCATIONS.DETROIT);
	const [exploreFleet, setExploreFleet] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);

	const aircraftType = selectedCategory?.aircraft_type_list?.[activeIndex] ?? null;
	const isPartner = aircraftType?.aircraft_provider === AIRCRAFT_PROVIDER.PARTNER || !exploreFleet;
	const aircraftTypeLength = selectedCategory?.aircraft_type_list?.length ?? 0;

	const contentWrapperRef = useCallback((node: HTMLDivElement) => {
		if (node) {
			const handleClickOutside = (event: MouseEvent) => {
				const btn = document.querySelector("[data-aircraft-dropdown-trigger]");

				if (node && !node.contains(event.target as any) && !btn?.contains(event.target as any)) {
					onUpdateOpenAircraft(false);
				}
			};

			document.addEventListener("mousedown", handleClickOutside);
			return () => {
				document.removeEventListener("mousedown", handleClickOutside);
			};
		}
	}, []);

	useEffect(() => {
		if (swiperRef.current) {
			swiperRef.current.slideTo(activeIndex);
		}
	}, [activeIndex]);

	useEffect(() => {
		if (!open) {
			setSelectedCategory(aircraft_categories?.[0] ?? null);
			setLocation(AIRCRAFT_LOCATIONS.DETROIT);
			setExploreFleet(false);
			setActiveIndex(0);
		}
	}, [open]);

	const handleSelectCategory = (c: any) => {
		setSelectedCategory(c);
		setActiveIndex(0);
	};

	const handleNavigateToTrustedPartners = () => {
		onUpdateOpenAircraft(false);
		if (isPartner) {
			router.push(PATH_ROUTES.PATH_TRUSTED_PARTNERS);
		} else {
			if (aircraftType) {
				router.push(`${PATH_ROUTES.PATH_AIRCRAFT_TYPES}/${aircraftType.slug}`);
			}
		}
	};

	const getCategoryStyle = (c: any, index: number, aircraft_categories: any[], selectedCategory: any) => ({
		width: `${100 / aircraft_categories.length}%`,
		borderColor: selectedCategory?._id === c._id ? "#ffb11a" : "transparent",
		background:
			selectedCategory?._id === c._id
				? "radial-gradient(50.2% 85.42% at 50.16% 100%, #FFDD9C 0%, #FFF6E2 40.87%, #FFF 100%)"
				: "#f5f5f5",
		borderTopLeftRadius: index === 0 ? 48 : 0,
		borderBottomLeftRadius: index === 0 ? 48 : 0,
		borderTopRightRadius: index === aircraft_categories.length - 1 ? 48 : 0,
		borderBottomRightRadius: index === aircraft_categories.length - 1 ? 48 : 0,
	});

	return (
		<div
			className={s.container}
			style={{
				visibility: open ? "visible" : "hidden",
				opacity: open ? 1 : 0,
			}}
		>
			<div
				className={s.content_wrapper}
				style={{
					transform: open ? "none" : "translateY(-100%)",
				}}
				ref={contentWrapperRef}
			>
				<Image
					src="/images/header/aircrafts/bg.png"
					alt="aircraft"
					fill
					sizes="100vw"
					style={{
						objectFit: "cover",
						objectPosition: "center bottom",
					}}
				/>
				<div className={s.content}>
					<div className={s.content_inner}>
						<div className={s.category_info_wrapper}>
							{exploreFleet && (
								<div className={s.explore_fleet_wrapper}>
									<div className={s.explore_fleet_content}>
										<button
											className={s.swiper_button_prev}
											style={{
												visibility: aircraftTypeLength > 1 ? "visible" : "hidden",
											}}
										>
											<SEChevronLeft />
										</button>
										<button
											className={s.swiper_button_next}
											style={{
												visibility: aircraftTypeLength > 1 ? "visible" : "hidden",
											}}
										>
											<SEChevronRight />
										</button>
										<Swiper
											effect={"coverflow"}
											grabCursor={true}
											centeredSlides={xl ? true : false}
											slidesPerView={xl ? 3 : 2}
											slidesPerGroup={1}
											coverflowEffect={{
												slideShadows: false,
												rotate: 0,
												stretch: -120,
												depth: 300,
												scale: 1,
											}}
											speed={1000}
											modules={[EffectCoverflow, Navigation]}
											navigation={{
												prevEl: `.${s.swiper_button_prev}`,
												nextEl: `.${s.swiper_button_next}`,
											}}
											onSwiper={(w) => {
												swiperRef.current = w;
											}}
											onSlideChange={(w) => setActiveIndex(w.activeIndex)}
											onBeforeDestroy={() => {
												swiperRef.current = null;
											}}
											style={{ height: "100%" }}
										>
											{selectedCategory?.aircraft_type_list?.map((item: any) => {
												const mainImage =
													item.images?.find((e) => e.type === "MAIN")?.url ??
													item.images?.[0]?.url ??
													"/images/example/aircraft_light_turbo.png";

												return (
													<SwiperSlide
														key={item._id}
														className={s.explore_fleet_swiper_slide}
													>
														<Flex
															alignItems="center"
															gap={12}
															style={{
																height: "100%",
															}}
														>
															<div
																style={{
																	width: "100%",
																}}
															>
																<Flex
																	alignItems="center"
																	gap={8}
																	className={s.explore_fleet_swiper_slide_header}
																>
																	<AircraftOwnershipTag isPartner={isPartner} />
																	<RATypography variant="sub-heading3">
																		{item.name}
																	</RATypography>
																</Flex>
																<div
																	style={{
																		position: "relative",
																		marginBlock: "32px 24px",
																	}}
																>
																	<img
																		width="100%"
																		src={mainImage}
																		alt={item.name}
																		style={{
																			objectFit: "contain",
																			objectPosition: "center",
																		}}
																	/>
																	<div
																		className={
																			s.explore_fleet_swiper_slide_name_overlay
																		}
																	>
																		<RATypography
																			variant="sub-heading3"
																			align="center"
																		>
																			{item.name}
																		</RATypography>
																	</div>
																</div>
																<Flex
																	gap={12}
																	alignItems="center"
																	justifyContent="center"
																	className={s.explore_fleet_swiper_slide_info}
																>
																	<Flex gap={16}>
																		<SEUserGroup />
																		<div
																			style={{
																				width: 100,
																			}}
																		>
																			<RATypography variant="gilda1">
																				{item.properties?.max_pax ?? 0}
																				{item.properties?.max_pax_symbol ?? ""}
																			</RATypography>
																			<RATypography variant="dm_sans1" top={2}>
																				{item.properties?.max_pax_unit}
																			</RATypography>
																		</div>
																	</Flex>
																	<Flex gap={16}>
																		<SEClock />
																		<div
																			style={{
																				width: 100,
																			}}
																		>
																			<RATypography variant="gilda1">
																				{item.properties?.max_fly_time ?? 0}
																				{item.properties?.max_fly_time_symbol ??
																					""}{" "}
																				{item.properties?.max_fly_time_unit}
																			</RATypography>
																			<RATypography variant="dm_sans1" top={2}>
																				Range
																			</RATypography>
																		</div>
																	</Flex>
																	<Flex gap={16}>
																		<SESpeedometer />
																		<div
																			style={{
																				width: 100,
																			}}
																		>
																			<RATypography variant="gilda1">
																				{item.properties?.cruising_speed ?? 0}
																				{item.properties
																					?.cruising_speed_symbol ?? ""}{" "}
																				{item.properties?.cruising_speed_unit}
																			</RATypography>
																			<RATypography variant="dm_sans1" top={2}>
																				Cruise Speed
																			</RATypography>
																		</div>
																	</Flex>
																</Flex>
															</div>
														</Flex>
													</SwiperSlide>
												);
											})}
											<SwiperSlide className={s.fake_swiper_slide} />
										</Swiper>
									</div>
								</div>
							)}
							<div
								className={s.category_world_map_wrapper}
								style={
									exploreFleet
										? { opacity: 0, visibility: "hidden" }
										: { opacity: 1, visibility: "visible" }
								}
							>
								<div className={s.category_info}>
									<div className={s.category_info_inner}>
										<Flex alignItems="center">
											<SEClock />
											<RATypography variant="gilda1" left={16}>
												{selectedCategory?.min_fly_times_hours ?? 0}-
												{selectedCategory?.max_fly_times_hours ?? 0} Hours
											</RATypography>
											<RATypography variant="dm_sans1" left={4}>
												Range
											</RATypography>
										</Flex>
										<Flex alignItems="center">
											<SEUserGroup />
											<RATypography variant="gilda1" left={16}>
												{selectedCategory?.min_passengers ?? 0}-
												{selectedCategory?.max_passengers ?? 0}
											</RATypography>
											<RATypography variant="dm_sans1" left={4}>
												PAX
											</RATypography>
										</Flex>
										<Flex alignItems="center">
											<SESpeedometer />
											<RATypography variant="gilda1" left={16}>
												{selectedCategory?.cruising_speed_mph ?? 0} Mph
											</RATypography>
											<RATypography variant="dm_sans1" left={4}>
												Cruise Speed
											</RATypography>
										</Flex>
									</div>
									<div className={s.category_info_locations}>
										{[
											AIRCRAFT_LOCATIONS.DETROIT,
											AIRCRAFT_LOCATIONS.MIAMI,
											AIRCRAFT_LOCATIONS.LOS_ANGELES,
										].map((loc) => (
											<button
												key={loc}
												className={s.location_button}
												style={{
													borderColor: location === loc ? "#ffb11a" : "#E9E8E8",
												}}
												onClick={() => setLocation(loc)}
											>
												{loc.replace(/_/g, " ")}
											</button>
										))}
									</div>
								</div>
								<div style={{ position: "relative", width: "100%", height: "100%" }}>
									<div className={s.category_map}>
										{selectedCategory && (
											<WorldMapRange location={location} type={selectedCategory.classtype} />
										)}
									</div>
								</div>
							</div>
						</div>
						<div className={s.explore_fleet_button_wrapper}>
							<RATypography variant="gilda3">{selectedCategory?.name}</RATypography>
							<Flex justifyContent="flex-end">
								<Flex gap={12} flexDirection="column">
									<button
										className={s.explore_royal_air_fleet_button}
										onClick={() => setExploreFleet(!exploreFleet)}
									>
										{exploreFleet ? (
											<>
												<SEChevronLeft />
												<span>Back To Category</span>
											</>
										) : (
											<>
												<SERAFleetFavicon />
												<span>Explore Royal Air’s Fleet</span>
												<SEChevronRight />
											</>
										)}
									</button>
									<button
										className={
											isPartner
												? s.explore_partner_fleet_button
												: s.explore_royal_air_fleet_button
										}
										onClick={handleNavigateToTrustedPartners}
									>
										{isPartner ? (
											<>
												<SEPartnerFleetFavicon />
												<span>Learn About Our Trusted Partners</span>
											</>
										) : (
											<>
												<SERAFleetFavicon />
												<span>Learn More About This Aircraft</span>
											</>
										)}
										<SEChevronRight />
									</button>
								</Flex>
							</Flex>
						</div>
						<div className={s.category_list}>
							{aircraft_categories.map((c: any, index: number) => (
								<div
									key={c._id}
									className={s.category_item}
									style={getCategoryStyle(c, index, aircraft_categories, selectedCategory)}
									onClick={() => handleSelectCategory(c)}
								>
									{c.name}
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
